import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom-v5';
import { Menu } from 'lucide-react';
import { useSidebarSafe } from '@/shared/ui/sidebar';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import Logo from '../../components/library/images/logo.svg';
import { useCurrentUser } from './useCurrentUser';
import { UserMenuPopup } from './UserMenuPopup';

type UserDetails = {
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  avatarUrl?: string;
};

const sidebarTriggerStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
};

const sidebarTriggerPlaceholderStyle: React.CSSProperties = { width: 32 };

const logoStyle: React.CSSProperties = { height: 22 };

const avatarWrapperStyle: React.CSSProperties = { position: 'relative' };

const avatarTriggerStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  outline: 'none',
};

function getInitials(user: UserDetails | undefined): string {
  if (!user) return '';
  const first = user.firstName?.[0] ?? '';
  const last = user.lastName?.[0] ?? '';
  const initials = `${first}${last}`.trim();
  if (initials) return initials.toUpperCase();
  return user.name?.[0]?.toUpperCase() ?? '';
}

const toggleOpen = (prev: boolean) => !prev;

type MobileHeaderProps = {
  leftAction?: React.ReactNode;
};

export function MobileHeader({ leftAction }: MobileHeaderProps = {}) {
  const user = useCurrentUser() as
    | (ReturnType<typeof useCurrentUser> & UserDetails)
    | undefined;
  const sidebarCtx = useSidebarSafe();
  const toggleSidebar = sidebarCtx?.toggleSidebar;
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleAvatarClick = useCallback(() => setOpen(toggleOpen), []);
  const handlePopupClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  if (!user) return null;

  const displayName =
    `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
    user.name ||
    '';

  return (
    <header className="lms-mobile-header">
      {/* Left zone */}
      {leftAction ?? (toggleSidebar ? (
        <button
          type="button"
          onClick={toggleSidebar}
          style={sidebarTriggerStyle}
          aria-label="Abrir menu"
        >
          <Menu size={20} strokeWidth={1} color="#0F172A" />
        </button>
      ) : (
        <div style={sidebarTriggerPlaceholderStyle} />
      ))}

      {/* Center zone — logo */}
      <Link to="/" className="flex items-center">
        <img src={Logo} alt="T2 Educação" style={logoStyle} />
      </Link>

      {/* Right zone — avatar */}
      <div className="flex items-center gap-3">
        <div style={avatarWrapperStyle}>
          <button
            ref={triggerRef}
            type="button"
            onClick={handleAvatarClick}
            style={avatarTriggerStyle}
          >
            <Avatar className="size-8 rounded-full">
              {user.avatarUrl && (
                <AvatarImage src={user.avatarUrl} alt={displayName} />
              )}
              <AvatarFallback className="rounded-full text-xs bg-[#EDEDED]">
                {getInitials(user)}
              </AvatarFallback>
            </Avatar>
          </button>

          {open && (
            <div ref={menuRef}>
              <UserMenuPopup
                displayName={displayName}
                email={user.email}
                onClose={handlePopupClose}
                className="absolute right-0 top-full mt-2 w-[240px]"
              />
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
